import type { AssignmentBuilderSchema, AssignmentBuilderSchemaInput } from '@repo/schemas/assignment/assignmentBuilder';
import type { QuizPublic } from '@repo/types/quiz';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCreateAssignment } from '@repo/api/assignment/useCreateAssignment';
import { useEditAssignment } from '@repo/api/assignment/useEditAssignment';
import { useGetAssignment } from '@repo/api/assignment/useGetAssignment';
import { useGetQuizTeacher } from '@repo/api/quiz/useGetQuizTeacher';
import { assignmentBuilderSchema } from '@repo/schemas/assignment/assignmentBuilder';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { useBeforeUnload } from '@/hooks/before-unload';
import { formErrorToast } from '@/utils/form/formErrorToast';

import { useAssignmentBuilderDraft } from './useAssignmentBuilderDraft';

const EMPTY_DRAFT: Partial<AssignmentBuilderSchemaInput> = {
  name: '',
  desc: '',
  available: null,
  dueTo: null,
  maxAttempts: 0,
  users: [],
  groups: [],
  quiz: null,
  autoGrade: false,
  gradeMapping: null,
};

export function useAssignmentBuilderForm() {
  const navigate = useNavigate();

  const { assignmentId: assignmentIdParam } = useParams<{
    assignmentId?: string;
  }>();
  const assignmentId = assignmentIdParam ? Number(assignmentIdParam) : undefined;
  const isEdit = assignmentId !== undefined && !Number.isNaN(assignmentId);

  const form = useForm<AssignmentBuilderSchemaInput, unknown, AssignmentBuilderSchema>({
    defaultValues: EMPTY_DRAFT,
    mode: 'onChange',
    resolver: standardSchemaResolver(assignmentBuilderSchema),
  });

  const { attachments, setAttachments, isDraftReady, clearDraft } = useAssignmentBuilderDraft({
    draftKey: 'assignment-builder-draft:new',
    form,
    emptyValues: EMPTY_DRAFT,
    enabled: !isEdit,
  });

  const assignmentQuery = useGetAssignment(assignmentId);

  // Snapshot of the loaded user/group/quiz ids so submit can compute diffs.
  const initialIdsRef = useRef<{
    users: number[];
    groups: number[];
    quizId: string | null;
  }>({
    users: [],
    groups: [],
    quizId: null,
  });
  const [hasResetFromRemote, setHasResetFromRemote] = useState<boolean>(false);

  useEffect(() => {
    if (!isEdit) return;
    if (hasResetFromRemote) return;
    if (!assignmentQuery.isSuccess) return;
    const remote = assignmentQuery.data;
    initialIdsRef.current = {
      users: remote.users.map((u) => u.id),
      groups: remote.groups.map((g) => g.id),
      quizId: remote.quiz?.id ?? null,
    };
    form.reset({
      name: remote.name,
      desc: remote.desc,
      available: remote.available,
      dueTo: remote.dueTo,
      maxAttempts: remote.maxAttempts,
      autoGrade: remote.autoGrade,
      gradeMapping: remote.gradeMapping ?? null,
      users: remote.users,
      groups: remote.groups,
      quiz: remote.quiz,
    });
    setHasResetFromRemote(true);
  }, [isEdit, hasResetFromRemote, assignmentQuery.isSuccess, assignmentQuery.data, form]);

  // With auto-grading on, the assignment is graded by the wrapped quiz, so its
  // scale is not edited but inherited from that quiz — keep them in sync (the
  // UI shows it read-only). With auto-grading off, the teacher edits it freely.
  const attachedQuiz = useWatch({ control: form.control, name: 'quiz' });
  const attachedQuizId = attachedQuiz?.id;
  const autoGrade = useWatch({ control: form.control, name: 'autoGrade' }) ?? false;
  const attachedQuizQuery = useGetQuizTeacher(attachedQuizId);

  useEffect(() => {
    if (!autoGrade || !attachedQuizId || !attachedQuizQuery.isSuccess) return;
    const quizMapping = attachedQuizQuery.data.gradeMapping ?? null;
    const current = form.getValues('gradeMapping') ?? null;
    if (JSON.stringify(current) !== JSON.stringify(quizMapping)) {
      form.setValue('gradeMapping', quizMapping, { shouldDirty: true });
    }
  }, [autoGrade, attachedQuizId, attachedQuizQuery.isSuccess, attachedQuizQuery.data, form]);

  const createAssignment = useCreateAssignment();
  const editAssignment = useEditAssignment();

  const onSubmit = (data: AssignmentBuilderSchema) => {
    if (isEdit && assignmentId !== undefined) {
      const { userIdArr, groupIdArr, quizId, ...rest } = data;
      const initial = initialIdsRef.current;
      editAssignment.mutate(
        {
          data: {
            id: assignmentId,
            ...rest,
            quizId: quizId === initial.quizId ? undefined : quizId,
            addUserIdArr: userIdArr.filter((id) => !initial.users.includes(id)),
            removeUserIdArr: initial.users.filter((id) => !userIdArr.includes(id)),
            addGroupIdArr: groupIdArr.filter((id) => !initial.groups.includes(id)),
            removeGroupIdArr: initial.groups.filter((id) => !groupIdArr.includes(id)),
          },
          addFiles: attachments.length > 0 ? attachments : undefined,
        },
        {
          onSuccess: () => {
            navigate(`/manage/assignments/${assignmentId}`);
          },
        }
      );
      return;
    }

    createAssignment.mutate(
      {
        data,
        attachments: attachments.length > 0 ? attachments : undefined,
      },
      {
        onSuccess: (created) => {
          clearDraft();
          navigate(`/manage/assignments/${created.id}`);
        },
      }
    );
  };

  const onInvalid = () => {
    formErrorToast(form, assignmentBuilderSchema);
  };

  const attachQuiz = (quiz: QuizPublic) => {
    form.setValue('quiz', { ...quiz, desc: quiz.desc ?? null, time: quiz.time ?? null }, { shouldDirty: true, shouldValidate: true });
    form.setValue('maxAttempts', 0, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('autoGrade', true, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const detachQuiz = () => {
    form.setValue('quiz', null, { shouldDirty: true, shouldValidate: true });
    form.setValue('autoGrade', false, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('gradeMapping', null, { shouldDirty: true });
  };

  const isPending = createAssignment.isPending || editAssignment.isPending;
  const isLoadingRemote = isEdit && !hasResetFromRemote;

  const isDirty = form.formState.isDirty;
  useBeforeUnload(isDirty && !isPending);

  const submitError = createAssignment.isError ? createAssignment.errorMessage : editAssignment.isError ? editAssignment.errorMessage : null;

  return {
    isEdit,
    form,
    attachments,
    setAttachments,
    isDraftReady,
    clearDraft,
    isDirty,
    onSubmit,
    onInvalid,
    attachQuiz,
    detachQuiz,
    isPending,
    isLoadingRemote,
    submitError,
  };
}
